import classNames from 'classnames/bind';
import Box from 'src/components/ui-kit/box/box';
import { ButtonVariant } from 'src/components/ui-kit/button/button';
import ButtonBlock from 'src/components/ui-kit/button/__block';
import FormCheckbox from 'src/components/ui-kit/form/_checkbox';
import { useFormik } from 'formik';
import { ListingsFiltersForm } from 'src/forms/listings-filters';
import { ChangeEvent, DetailedHTMLProps, HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './filters.module.scss';
import { useSelector } from 'react-redux';
import FormMultiSelect from 'src/components/ui-kit/form/_multiselect';
import { AuthSelectors } from 'src/store/auth';
import { flatten } from 'lodash';
import { useRouter } from 'next/router';
import { useRouterListings } from '../../../../hooks/use-router-listings';
import { MineralListingsPageSelectors } from '../../../../store/pages/mineral-listings';
import FormRadio, { RadioVariant } from 'src/components/ui-kit/form/_radio';
import { ListingFilterKeys } from 'src/enums/listing/filter-keys';
import Icon, { IconTheme, IconVariant } from 'src/components/ui-kit/icon/icon';

const classesContext = classNames.bind(styles);

export default function ListingsFilters({
  className,
  ...restProps
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>): JSX.Element {
  const { t } = useTranslation(['components:pages:listings:filters']);

  const router = useRouter();
  const routerListings = useRouterListings();
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const states = useSelector(MineralListingsPageSelectors.states);

  const handleSubmit = (values: ListingsFiltersForm): void => {
    const currentPageParams = routerListings.getCurrentPageParams();

    routerListings.push({
      page: null,
      orderBy: currentPageParams.orderBy ?? null,
      orderByDirection: currentPageParams.orderByDirection ?? null,
      filters: {
        ...values,
        statesIDs: values.statesIDs.map((stateID) => Number(stateID)),
        countiesIDs: values.countiesIDs.map((countyID) => Number(countyID)),
        isShowSalePending: values.isShowSalePending,
        isCashFlow: values.isCashFlow,
        listings: values.listings
      }
    });
  };

  const initialValues = new ListingsFiltersForm();
  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit
  });

  const statesOptions = states.map((item) => ({ value: item.id, label: item.name }));
  const countiesOptions = flatten(states.map((state) => state.counties))
    .filter((county) => formik.values.statesIDs.includes(county.stateID.toString()))
    .map((county) => ({ value: county.id, label: county.name }));

  const handleReset = (): void => {
    formik.resetForm();

    routerListings.push({
      page: null,
      orderBy: null,
      orderByDirection: null,
      filters: {
        isCashFlow: null,
        statesIDs: null,
        countiesIDs: null,
        listings: null,
        query: null
      }
    });
  };

  const handleSubmitOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    formik.handleChange(event);
    formik.submitForm();
  };

  const toggleMobileFilters = (): void => setIsFiltersOpen(!isFiltersOpen);

  useEffect(() => {
    const currentPageParams = routerListings.getCurrentPageParams();

    if (currentPageParams.filters) {
      formik.setValues({
        statesIDs: currentPageParams.filters?.statesIDs
          ? currentPageParams.filters.statesIDs.map((stateID) => stateID.toString())
          : [],
        countiesIDs: currentPageParams.filters?.countiesIDs
          ? currentPageParams.filters.countiesIDs.map((countyID) => countyID.toString())
          : [],
        isCashFlow: !!currentPageParams.filters?.isCashFlow,
        isShowSalePending: !!currentPageParams.filters?.isShowSalePending,
        listings: currentPageParams.filters?.listings ?? ListingFilterKeys.ACTIVE
      });
    }
  }, [router.query]);

  return (
    <Box
      className={classesContext(['filters', className])}
      {...restProps}>
      <div className={classesContext('filters_accordion', { filters_accordion__open: isFiltersOpen })}>
        <h3 className={classesContext('filters_title', 'filters_title__desktop')}>{t('TEXT_LISTING_FILTERS')}</h3>
        <div className={styles.filters_inner}>
          <h3 className={classesContext('filters_title', 'filters_title__tablet')}>
            {t('TEXT_LISTING_FILTERS')}
            <button
              onClick={toggleMobileFilters}
              className={styles.filters_button}>
              <Icon variant={isFiltersOpen ? IconVariant.CLOSE : IconVariant.FILTER} />
            </button>
          </h3>
        </div>
        <div className={styles.filters_content}>
          <form
            onSubmit={formik.handleSubmit}
            className={styles.filters_form}>
            <div className={styles.filters_checkboxes}>
              {isAuthenticated && (
                <>
                  <FormCheckbox
                    name='isShowSalePending'
                    onChange={handleSubmitOnChange}
                    checked={formik.values.isShowSalePending}
                    className={styles.filters_checkbox}
                  >
                    {t('TEXT_SHOW_SALE_PENDING')}
                  </FormCheckbox>
                  <FormCheckbox
                    name='isCashFlow'
                    onChange={handleSubmitOnChange}
                    checked={formik.values.isCashFlow}
                    className={styles.filters_checkbox}
                  >
                    {t('TEXT_WITH_CASH_FLOW_ONLY')}
                  </FormCheckbox>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <FormRadio
                    variant={RadioVariant.CHECKBOX}
                    value={ListingFilterKeys.ACTIVE}
                    name='listings'
                    checked={formik.values.listings === ListingFilterKeys.ACTIVE}
                    onChange={handleSubmitOnChange}
                    className={styles.filters_checkbox}
                  >
                    {t('TEXT_ACTIVE_LISTINGS')}
                  </FormRadio>
                  <FormRadio
                    variant={RadioVariant.CHECKBOX}
                    value={ListingFilterKeys.SOLD}
                    name='listings'
                    checked={formik.values.listings === ListingFilterKeys.SOLD}
                    onChange={handleSubmitOnChange}
                    className={styles.filters_checkbox}
                  >
                    {t('TEXT_SOLD_LISTINGS')}
                  </FormRadio>
                  <FormRadio
                    variant={RadioVariant.CHECKBOX}
                    value={ListingFilterKeys.HISTORICAL}
                    name='listings'
                    checked={formik.values.listings === ListingFilterKeys.HISTORICAL}
                    onChange={handleSubmitOnChange}
                    className={styles.filters_checkbox}
                  >
                    {t('TEXT_HISTORICAL')}
                  </FormRadio>
                </>
              )}
            </div>
            <div className='row row__middle'>
              <div className={classesContext('col col__33 col__tablet__50', 'filters_col__state')}>
                <h3 className={styles.filters_subtitle}>{t('TEXT_STATE')}</h3>
                <FormMultiSelect<ListingsFiltersForm>
                  name='statesIDs'
                  formik={formik}
                  options={statesOptions}
                  className={styles.filters_multiselect}
                />
              </div>
              <div className='col col__33 col__tablet__50'>
                <h3 className={styles.filters_subtitle}>{t('TEXT_COUNTRY')}</h3>
                <FormMultiSelect<ListingsFiltersForm>
                  name='countiesIDs'
                  formik={formik}
                  options={countiesOptions}
                  className={styles.filters_multiselect}
                />
              </div>
              <div className='col col__33 col__tablet__100'>
                <div className={styles.filters_actions}>
                  <ButtonBlock
                    type='submit'
                    variant={ButtonVariant.SECONDARY}>
                    {t('BUTTON_SHOW_LISTINGS')}
                    <span className={styles.filters_actions_icon}>
                      <Icon
                        variant={IconVariant.NEXT}
                        theme={IconTheme.WHITE} />
                    </span>
                  </ButtonBlock>
                  <ButtonBlock
                    type='reset'
                    onClick={handleReset}
                    variant={ButtonVariant.PRIMARY}>
                    {t('BUTTON_RESET_FILTERS')}
                  </ButtonBlock>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Box>
  );
}
